// src/pages/setup/SetupStep2.tsx - 採用 Welcome Page2-3 設計模式
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Target, ArrowLeft } from 'lucide-react';
import { markPersonalizationStatus } from '../../services/auth';
import { logDev } from '../../utils/logger';
import { logEvent } from '../../utils/analytics';

const SetupStep2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 從 navigation state 取得前一步的答案
  const prevAnswers = location.state as { q1?: string } | null;



  const handleNext = () => {
    if (answer.trim()) {
      logDev('SetupStep2', '用戶完成第二題', { answerLength: answer.length });

      // 追蹤：完成步驟 2
      try {
        logEvent('personalization_step_completed', { step: 2 });
      } catch (error) {
        // 靜默失敗
      }
    }
    // 傳遞所有答案到下一步
    const answers = {
      q1: prevAnswers?.q1 || '',
      q2: answer.trim()
    };
    navigate('/setup/step-3', { state: answers });
  };

  const handleBack = () => {
    // 回到上一步時傳遞現有答案以保持狀態
    if (prevAnswers?.q1) {
      navigate('/setup/step-1', { state: { q1: prevAnswers.q1 } });
    } else {
      navigate('/setup/step-1');
    }
  };

  const handleSkip = () => {
    // 傳遞現有 prevAnswers 到下一步，確保 Step3 能看到 Q1 的答案
    logDev('SetupStep2', '用戶跳過第二題，進入第三題');
    navigate('/setup/step-3', { state: prevAnswers });
  };

  const isValid = answer.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Progress Dots */}
      <div className="flex justify-center space-x-2 pt-4 pb-2">
        {Array.from({ length: 3 }, (_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === 2;
          const isCompleted = stepNumber < 2;

          return (
            <React.Fragment key={stepNumber}>
              <div
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-sky-500 to-sky-500 transform scale-125 animate-welcome-pulse-glow'
                    : isCompleted
                    ? 'bg-gradient-to-r from-sky-500 to-sky-500'
                    : 'bg-gray-300'
                }`}
              />
              {stepNumber < 3 && (
                <div className={`w-12 h-0.5 transition-colors duration-300 ${
                  stepNumber < 2 ? 'bg-gradient-to-r from-sky-500 to-sky-500' : 'bg-gray-300'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-2">
        <div className="w-full max-w-sm mx-auto">
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center mt-4 animate-welcome-slide-up">
              {/* Icon - 獨立視覺焦點 */}
              <div className="mb-8">
                <div className="relative w-16 h-16 mx-auto bg-gradient-to-br from-sky-500 to-green-600 rounded-2xl flex items-center justify-center">
                  <Target className="w-8 h-8 text-white" />
                  <div className="absolute w-20 h-20 rounded-2xl border-2 border-sky-400 opacity-30 animate-welcome-pulse-glow"></div>
                </div>
              </div>

              {/* 標題 + 副標題群組 - 緊密文字說明 */}
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-gray-900 animate-welcome-slide-up delay-200">
                  Key focus for 3 months?
                </h2>
                <p className="text-gray-600 px-2 animate-welcome-slide-up delay-400">
                  Share one or two main objectives
                </p>
              </div>
            </div>

            {/* Input Container */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border-2 border-gray-200 animate-welcome-slide-up delay-600 opacity-0">
              <div className="relative mb-4">
                <textarea
                  placeholder="Launch my product, run a half-marathon…"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-sky-400 focus:shadow-lg transition-all text-gray-700 placeholder-gray-400 bg-white"
                  rows={4}
                />
                <div className="absolute bottom-3 right-3 text-xs text-sky-400">
                  <span className="animate-typing-blink">|</span>
                </div>
              </div>

            </div>

            {/* Buttons - 使用統一 Apple 樣式 */}
            <div className="space-y-3">
              <button
                onClick={handleNext}
                disabled={!isValid}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-lg ${
                  isValid
                    ? 'btn-apple-primary'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isValid ? 'Continue →' : 'Share your goals'}
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleBack}
                  className="btn-apple-secondary py-3 px-4 rounded-xl font-medium flex items-center justify-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </button>
                <button
                  onClick={handleSkip}
                  className="btn-apple-secondary py-3 px-4 rounded-xl font-medium"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Safe Area */}
      <div className="pb-4"></div>
    </div>
  );
};

export default SetupStep2;